import { PublicNav, PublicFooter } from './PricingPage'

/**
 * Single page that renders Terms / Privacy / Refunds based on the `kind`
 * prop passed from App.jsx. Three routes share this component to avoid
 * duplicating the chrome (nav + footer) and keep the legal copy in one
 * place.
 *
 * The content is consultor-friendly Spanish, not lawyer-grade. Treat this
 * as a starting point — for production you should review with an abogado.
 */
export default function LegalPage({ kind = 'terms' }) {
  const page = kind === 'privacy' ? privacy : kind === 'refunds' ? refunds : terms

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      <article className="max-w-3xl mx-auto px-6 py-14 prose prose-sm prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600">
        <p className="text-xs uppercase tracking-wider text-gray-400">Documento legal · {page.lastUpdated}</p>
        <h1 className="text-3xl font-bold mt-1 mb-6">{page.title}</h1>
        {page.body}
      </article>
      <PublicFooter />
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Each `body` is JSX so we keep formatting consistent with the rest of the
// app. The text is intentionally short and plain — easier to maintain and
// less likely to embed liability we didn't intend.
// ──────────────────────────────────────────────────────────────────────────

const COMPANY = 'Next Valley Consultants S.A.S.'
const CONTACT = 'estrategia@nextvalleyco.com'

const terms = {
  title: 'Términos y condiciones',
  lastUpdated: 'Vigente desde abril de 2026',
  body: (
    <>
      <p>Estos términos regulan el uso de la aplicación <strong>Estrategia en una Caja</strong> ("la App"), operada por {COMPANY} en <a href="https://www.nvc-seguimiento.com">www.nvc-seguimiento.com</a>. Al registrarte aceptas estos términos.</p>

      <h2>1. Qué ofrecemos</h2>
      <p>La App es una herramienta de planeación estratégica guiada por inteligencia artificial, basada en la metodología propietaria de {COMPANY}. Permite a empresas construir su plan estratégico en formato auto-servicio.</p>

      <h2>2. Cuenta y acceso</h2>
      <ul>
        <li>Eres responsable de la veracidad de la información que proporciones al registrarte.</li>
        <li>Eres responsable de la confidencialidad de tu contraseña.</li>
        <li>Las cuentas son personales o representan a una empresa específica; no se pueden compartir credenciales con terceros.</li>
      </ul>

      <h2>3. Suscripción y pagos</h2>
      <ul>
        <li>Los planes Mensual y Anual se cobran por adelantado en USD (procesados en COP por MercadoPago).</li>
        <li>El plan Mensual se renueva automáticamente cada mes salvo que lo canceles desde tu cuenta.</li>
        <li>El plan Anual no tiene renovación automática; al expirar puedes renovarlo manualmente.</li>
        <li>Los packs de sesiones con consultor son compras únicas; los créditos no caducan.</li>
      </ul>

      <h2>4. Cancelación</h2>
      <p>Puedes cancelar tu suscripción en cualquier momento. Mantendrás acceso a la App hasta el final del período pagado. No emitimos reembolsos prorrateados por cancelación; ver Política de reembolsos.</p>

      <h2>5. Propiedad intelectual</h2>
      <ul>
        <li>El contenido que tú generes (tu plan estratégico, tus datos) es tuyo.</li>
        <li>La metodología, el código de la App, los textos de la interfaz y los prompts del agente de IA son propiedad de {COMPANY}.</li>
        <li>No puedes redistribuir ni revender la App sin autorización escrita.</li>
      </ul>

      <h2>6. Limitación de responsabilidad</h2>
      <p>La App es una herramienta de apoyo a la planeación estratégica. Las decisiones de negocio que tomes con base en su uso son tu responsabilidad. {COMPANY} no garantiza resultados específicos ni se hace responsable de pérdidas derivadas de decisiones empresariales.</p>

      <h2>7. Cambios al servicio</h2>
      <p>Podemos actualizar estos términos avisando con 30 días de anticipación por correo electrónico al usuario. Si los cambios te afectan negativamente, puedes cancelar antes de que entren en vigor con reembolso prorrateado.</p>

      <h2>8. Ley aplicable</h2>
      <p>Estos términos se rigen por las leyes de la República de Colombia. Cualquier disputa se resolverá ante los tribunales competentes de Cali, Colombia.</p>

      <p className="text-xs mt-8 text-gray-400">Para cualquier consulta sobre estos términos: <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </>
  ),
}

const privacy = {
  title: 'Política de privacidad',
  lastUpdated: 'Vigente desde abril de 2026',
  body: (
    <>
      <p>{COMPANY} respeta tu privacidad. Esta política explica qué datos recogemos, cómo los usamos y tus derechos.</p>

      <h2>1. Qué recogemos</h2>
      <ul>
        <li><strong>Datos de cuenta</strong>: nombre, email, contraseña (hasheada), empresa, industria, país, tamaño.</li>
        <li><strong>Contenido del plan</strong>: lo que escribes en cada sección, archivos que subes, comentarios.</li>
        <li><strong>Datos de pago</strong>: procesados por MercadoPago. No almacenamos números de tarjeta en nuestros servidores.</li>
        <li><strong>Telemetría</strong>: páginas visitadas, eventos de conversión (registro, paywall visto, plan descargado). Usamos PostHog para esto.</li>
      </ul>

      <h2>2. Cómo lo usamos</h2>
      <ul>
        <li>Operar la App y permitirte construir tu plan.</li>
        <li>Procesar pagos y enviarte recibos.</li>
        <li>Enviarte correos transaccionales (bienvenida, recordatorios, alertas del agente IA).</li>
        <li>Mejorar la App basándonos en patrones de uso agregados.</li>
      </ul>

      <h2>3. Lo que NO hacemos</h2>
      <ul>
        <li>NO vendemos tus datos a terceros.</li>
        <li>NO usamos tu contenido para entrenar modelos de IA propios o de terceros.</li>
        <li>NO compartimos tu plan estratégico con otros usuarios.</li>
      </ul>

      <h2>4. Procesadores de datos</h2>
      <p>Usamos los siguientes proveedores que pueden procesar tus datos para operar la App:</p>
      <ul>
        <li><strong>Supabase</strong> — base de datos y autenticación.</li>
        <li><strong>Vercel</strong> — hosting del frontend.</li>
        <li><strong>MercadoPago</strong> — procesamiento de pagos.</li>
        <li><strong>Resend</strong> — envío de correos electrónicos transaccionales.</li>
        <li><strong>Anthropic (Claude)</strong> — generación de respuestas del agente IA. El contenido enviado a Claude no se usa para entrenar modelos según los términos de Anthropic.</li>
        <li><strong>PostHog</strong> — analítica de producto agregada.</li>
        <li><strong>Calendly</strong> — agendamiento de sesiones (solo si compras un pack).</li>
      </ul>

      <h2>5. Tus derechos</h2>
      <ul>
        <li>Puedes exportar tu plan estratégico completo en cualquier momento desde tu dashboard.</li>
        <li>Puedes solicitar la eliminación de tu cuenta y todos tus datos escribiendo a <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</li>
        <li>Puedes corregir cualquier dato personal incorrecto desde la configuración de tu cuenta.</li>
      </ul>

      <h2>6. Retención</h2>
      <p>Mantenemos tu plan estratégico mientras tu cuenta esté activa. Si cancelas, conservamos los datos por 90 días por si decides reactivar. Después se eliminan de forma irrecuperable.</p>

      <h2>7. Cookies y rastreo</h2>
      <p>Usamos cookies estrictamente necesarias para la sesión de usuario. La analítica usa identificadores anónimos sin cookies de terceros.</p>

      <h2>8. Contacto</h2>
      <p>Para ejercer tus derechos o cualquier duda sobre privacidad: <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </>
  ),
}

const refunds = {
  title: 'Política de reembolsos',
  lastUpdated: 'Vigente desde abril de 2026',
  body: (
    <>
      <p>Queremos que estés satisfecho con la App. Esta es nuestra política de reembolsos.</p>

      <h2>1. Garantía de 7 días — primera compra</h2>
      <p>Si es tu primera compra (Plan Mensual o Anual) y no estás satisfecho, te devolvemos el 100% dentro de los <strong>primeros 7 días calendario</strong> después del pago. Sin preguntas.</p>
      <p>Para solicitarlo, escribe a <a href={`mailto:${CONTACT}`}>{CONTACT}</a> con tu correo de cuenta y el comprobante de pago.</p>

      <h2>2. Plan Mensual — después del trial</h2>
      <p>Si cancelas un plan mensual después del día 7, mantienes acceso hasta el final del mes pagado. No reembolsamos meses ya pagados.</p>

      <h2>3. Plan Anual — después de 7 días</h2>
      <p>El plan anual no se reembolsa de forma automática. Si necesitas cancelar por circunstancias excepcionales (cierre de empresa, error de compra), evaluaremos caso por caso un reembolso prorrateado.</p>

      <h2>4. Packs de sesiones con consultor</h2>
      <ul>
        <li>Si NO has usado ninguna sesión: reembolso del 100% dentro de 14 días, 50% después.</li>
        <li>Si usaste 1 o más sesiones: reembolso solo de las sesiones no consumidas, prorrateado.</li>
        <li>Las sesiones agendadas que canceles con menos de 24 horas de anticipación se consideran consumidas.</li>
      </ul>

      <h2>5. Cómo se procesa el reembolso</h2>
      <p>Los reembolsos se procesan por MercadoPago a la misma tarjeta o cuenta usada para el pago. El plazo desde la aprobación es de 5 a 10 días hábiles, dependiendo del banco emisor.</p>

      <h2>6. Disputas</h2>
      <p>Si no llegamos a un acuerdo, puedes acudir a la Superintendencia de Industria y Comercio de Colombia o al mecanismo de resolución de disputas de MercadoPago.</p>

      <p className="text-xs mt-8 text-gray-400">Cualquier solicitud de reembolso: <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </>
  ),
}
